import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, of, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { LazyScrollableDataSource } from '@rappi/common';
import { SelectComponent } from '@rappi/ui/select';

import { MakersFinancesMgrService } from '../../../../services/makers-finances-mgr.service';
import { StoreAuxService } from '../../../../../services/storeAux/store-aux.service';
import { Company, NewPurchaseOrderConfig, PurchaseOrderDetail, PurchaseOrderCompanyDetail, PurchaseOrderContactDetail } from '../../../../models/makers-finances-mgr.models';
import {
  DetailMode, SourceType,
  PO_FIELD, PO_FIELD_LABELS, PO_SECTION_LABELS, PO_SECTION,
  SEARCH_SELECT_FIELDS,
  SOURCE_TYPE, COMPANY_OC_REQUIRED_TOOLTIP,
} from '../../purchase-order.constants';
import { SelectOption, staticSourceFromOptions } from '../section-base.types';

@Component({
  selector: 'app-section-buyer-info',
  templateUrl: './section-buyer-info.component.html',
  styleUrls: ['./section-buyer-info.component.scss'],
})
export class SectionBuyerInfoComponent implements OnInit, OnChanges, OnDestroy {
  @Input() form: FormGroup;
  @Input() mode: DetailMode;
  @Input() sourceType: SourceType;
  @Input() remoteConfig: NewPurchaseOrderConfig | null;
  @Input() submitted = false;
  @Input() fieldConfigs: Record<string, any> = {};
  // Flips true once the parent has patched the form from the loaded PO; triggers setSelection on
  // the company/contact/agency selects (one-select is not form-bound).
  @Input() poLoaded = false;
  // The loaded PO detail — used to read nested company_to_bill / contact / agency objects so the
  // selects can be bound without extra search API calls.
  @Input() poDetail: PurchaseOrderDetail | null = null;

  @ViewChild('companySelect') private readonly companySelect: SelectComponent<SelectOption>;
  @ViewChild('pocSelect') private readonly pocSelect: SelectComponent<SelectOption>;
  @ViewChild('agencySelect') private readonly agencySelect: SelectComponent<SelectOption>;
  @ViewChild('agencyRecognitionSelect') private readonly agencyRecognitionSelect: SelectComponent<SelectOption>;

  readonly F = PO_FIELD;
  readonly LABELS = PO_FIELD_LABELS;
  readonly sectionLabel = PO_SECTION_LABELS[PO_SECTION.BUYER_INFORMATION];

  readonly searchSelectConfig = { search: true, includeAll: false, showTotalsChip: false, maxNumberOfChips: 1, panelClass: '' };
  readonly selectConfig = { search: false, includeAll: false, showTotalsChip: false, maxNumberOfChips: 1, panelClass: '' };

  selectSources: Record<string, LazyScrollableDataSource<SelectOption>> = {};

  private readonly _contactCompanyId$ = new BehaviorSubject<string | null>(null);
  private readonly _companyCache = new Map<string, Company>();
  private readonly destroy$ = new Subject<void>();
  private _agencyRequired = false;

  constructor(
    private readonly financesService: MakersFinancesMgrService,
    private readonly storeAux: StoreAuxService,
  ) {}

  ngOnInit(): void {
    this._rebuildCompanyAndAgencyDatasources();

    this.form.get(PO_FIELD.COMPANY_TO_BILL)?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(companyId => {
        this._contactCompanyId$.next(companyId || null);
        (this.selectSources[PO_FIELD.POINT_OF_CONTACT] as LazyScrollableDataSource<SelectOption>)?.setFilterValue('');
        this.form.get(PO_FIELD.POINT_OF_CONTACT)?.setValue(null, { emitEvent: false });
        this.pocSelect?.clearSelection();
      });

    this.form.get(PO_FIELD.AGENCY)?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this._updateAgencyRecognitionValidator());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['remoteConfig'] && this.remoteConfig?.agency_recognition_method_options?.length) {
      this.selectSources[PO_FIELD.AGENCY_RECOGNITION_METHOD] = staticSourceFromOptions(
        this.remoteConfig.agency_recognition_method_options
      );
      this._updateAgencyRecognitionValidator();
    }

    if (changes['sourceType']) {
      if (!changes['sourceType'].firstChange) {
        this._rebuildCompanyAndAgencyDatasources();
        if (this.remoteConfig?.agency_recognition_method_options?.length) {
          this.selectSources[PO_FIELD.AGENCY_RECOGNITION_METHOD] = staticSourceFromOptions(
            this.remoteConfig.agency_recognition_method_options
          );
        }
      }

    }

    if (changes['poLoaded'] && this.poLoaded) {
      setTimeout(() => this._prefillSelects());
    }
  }

  // Edit mode: sync the company/contact/agency select UIs to the patched form values. When the
  // detail endpoint returns nested objects (company_to_bill, contact, agency), they are used
  // directly — no extra search API calls. Order matters: company first — setting it via
  // setSelection triggers onSelectChange → _onCompanyToBillSelected (sets agency validators / may
  // auto-set agency for agency-type companies), then point of contact, then agency (overriding any
  // auto-set value if the form already holds a different agency), then agency recognition method.
  private _prefillSelects(): void {
    const companyId = this.form.get(PO_FIELD.COMPANY_TO_BILL)?.value;
    const contactId = this.form.get(PO_FIELD.POINT_OF_CONTACT)?.value;
    const agencyId = this.form.get(PO_FIELD.AGENCY)?.value;
    const recognitionMethod = this.form.get(PO_FIELD.AGENCY_RECOGNITION_METHOD)?.value;

    const nestedCompany = this.poDetail?.company_to_bill ?? null;
    const nestedContact = this.poDetail?.contact ?? null;
    const nestedAgency = this.poDetail?.agency ?? null;

    if (companyId) {
      if (nestedCompany && nestedCompany.id === companyId) {
        const company = this._toCompany(nestedCompany);
        this._companyCache.set(company.id, company);
        this.companySelect?.setSelection({ id: company.id, name: `${company.maker_code}  ${company.name}` });
        this._onCompanyToBillSelected(company);
        this._prefillContact(nestedContact, contactId);
        this._prefillAgency(nestedAgency, agencyId);
        this._prefillAgencyRecognition(recognitionMethod);
      } else {
        // Fallback: resolve via search API (older API responses without nested objects).
        this.financesService.searchCompanies(this.storeAux.country, companyId)
          .pipe(takeUntil(this.destroy$))
          .subscribe(res => {
            const company = res.data.find(c => c.id === companyId);
            if (!company) {
              return;
            }
            this._companyCache.set(company.id, company);
            this.companySelect?.setSelection({ id: company.id, name: `${company.maker_code}  ${company.name}` });
            this._onCompanyToBillSelected(company);
            this._prefillContact(null, contactId);
            this._prefillAgency(null, agencyId);
            this._prefillAgencyRecognition(recognitionMethod);
          });
      }
    } else {
      this._prefillAgency(nestedAgency, agencyId);
      this._prefillAgencyRecognition(recognitionMethod);
    }
  }

  private _toCompany(detail: PurchaseOrderCompanyDetail): Company {
    return {
      id: detail.id,
      name: detail.name,
      maker_code: detail.maker_code ?? '',
      country: this.storeAux.country,
      type_of_company: detail.type_of_company ?? '',
      status: '',
    };
  }

  private _prefillContact(nested: PurchaseOrderContactDetail | null, contactId: string | null): void {
    if (!contactId) {
      return;
    }
    if (nested && nested.id === contactId) {
      this.pocSelect?.setSelection({ id: nested.id, name: `${nested.name}  ·  ${nested.email ?? ''}` });
      return;
    }
    // Fallback: resolve via search API.
    const companyId = this.form.get(PO_FIELD.COMPANY_TO_BILL)?.value;
    if (!companyId) {
      return;
    }
    this.financesService.searchContacts(companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        const contact = res.data.find(c => c.id === contactId);
        if (contact) {
          this.pocSelect?.setSelection({ id: contact.id, name: `${contact.name}  ·  ${contact.email}` });
        }
      });
  }

  private _prefillAgency(nested: PurchaseOrderCompanyDetail | null, agencyId: string | null): void {
    if (!agencyId) {
      return;
    }
    // If the agency differs from what _onCompanyToBillSelected auto-set, override it with the
    // loaded value.
    this.form.get(PO_FIELD.AGENCY)?.setValue(agencyId, { emitEvent: false });
    if (nested && nested.id === agencyId) {
      const agency = this._toCompany(nested);
      this._companyCache.set(agency.id, agency);
      this.agencySelect?.setSelection({ id: agency.id, name: `${agency.maker_code}  ${agency.name}` });
      return;
    }
    // Fallback: resolve via search API.
    this.financesService.searchCompanies(this.storeAux.country, agencyId, { agencyOnly: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        const agency = res.data.find(c => c.id === agencyId);
        if (agency) {
          this._companyCache.set(agency.id, agency);
          this.agencySelect?.setSelection({ id: agency.id, name: `${agency.maker_code}  ${agency.name}` });
        }
      });
  }

  private _prefillAgencyRecognition(method: string | null): void {
    if (!method || !this.agencyRecognitionSelect || !this.remoteConfig?.agency_recognition_method_options?.length) {
      return;
    }
    const match = this.remoteConfig.agency_recognition_method_options.find(o => o.value === method);
    if (match) {
      this.agencyRecognitionSelect.setSelection({ id: match.value, name: match.label });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isPointOfContactDisabled(): boolean {
    return !this.form?.get(PO_FIELD.COMPANY_TO_BILL)?.value;
  }

  get isAgencyRecognitionVisible(): boolean {
    return this.storeAux.country === 'CO'
      && !!this.form?.get(PO_FIELD.AGENCY)?.value
      && !!(this.remoteConfig?.agency_recognition_method_options?.length);
  }

  get pointOfContactLabel(): string {
    const base = this.LABELS[this.F.POINT_OF_CONTACT];
    const isRequired = !!this.fieldConfigs[PO_FIELD.POINT_OF_CONTACT]?.mandatory_by_source?.[this.sourceType];
    return isRequired ? base : `${base} (Opcional)`;
  }

  get agencyLabel(): string {
    const base = this.LABELS[this.F.AGENCY];
    return this._agencyRequired ? base : `${base} (Opcional)`;
  }

  getSelectConfig(fieldKey: string): object {
    return (SEARCH_SELECT_FIELDS as ReadonlyArray<string>).includes(fieldKey)
      ? this.searchSelectConfig
      : this.selectConfig;
  }

  onSelectChange(fieldKey: string, value: SelectOption): void {
    this.form.get(fieldKey)?.setValue(value?.id ?? null);

    if (fieldKey === PO_FIELD.COMPANY_TO_BILL) {
      const company = value?.id ? this._companyCache.get(value.id) : null;
      this._onCompanyToBillSelected(company ?? null);
    }

    if (fieldKey === PO_FIELD.AGENCY && !value?.id) {
      this.form.get(PO_FIELD.AGENCY_RECOGNITION_METHOD)?.setValue(null, { emitEvent: false });
    }
  }

  private _onCompanyToBillSelected(company: Company | null): void {
    const isAgencyType = company?.type_of_company === 'Agency';
    const agencyControl = this.form.get(PO_FIELD.AGENCY);

    if (isAgencyType && !agencyControl?.value && company) {
      const option: SelectOption = { id: company.id, name: `${company.maker_code}  ${company.name}` };
      agencyControl?.setValue(company.id, { emitEvent: false });
      this.agencySelect?.setSelection(option);
    }

    this._agencyRequired = isAgencyType;
    if (isAgencyType) {
      agencyControl?.setValidators([Validators.required]);
    } else {
      agencyControl?.clearValidators();
    }
    agencyControl?.updateValueAndValidity({ emitEvent: false });
  }

  private _updateAgencyRecognitionValidator(): void {
    const ctrl = this.form?.get(PO_FIELD.AGENCY_RECOGNITION_METHOD);
    if (this.isAgencyRecognitionVisible) {
      ctrl?.setValidators([Validators.required]);
    } else {
      ctrl?.clearValidators();
    }
    ctrl?.updateValueAndValidity({ emitEvent: false });
  }

  isInvalid(key: string): boolean {
    if (!this.submitted) {
      return false;
    }
    const ctrl = this.form.get(key);
    return !ctrl?.value || !!ctrl.errors?.['required'];
  }

  isAgencyInvalid(): boolean {
    if (!this.submitted) {
      return false;
    }
    return !!this.form.get(this.F.AGENCY)?.errors?.['required'];
  }

  private _rebuildCompanyAndAgencyDatasources(): void {
    this.selectSources[PO_FIELD.COMPANY_TO_BILL] = new LazyScrollableDataSource<SelectOption>(
      (page, _limit, search) => {
        if (page > 0) {
          return of([]);
        }
        return this.financesService.searchCompanies(this.storeAux.country, search || undefined)
          .pipe(map(res => {
            res.data.forEach(c => this._companyCache.set(c.id, c));
            return res.data.map(c => this._toCompanyOption(c));
          }));
      }
    );

    this.selectSources[PO_FIELD.AGENCY] = new LazyScrollableDataSource<SelectOption>(
      (page, _limit, search) => {
        if (page > 0) {
          return of([]);
        }
        return this.financesService.searchCompanies(this.storeAux.country, search || undefined, { agencyOnly: true })
          .pipe(map(res => {
            res.data.forEach(c => this._companyCache.set(c.id, c));
            return res.data.map(c => ({ id: c.id, name: `${c.maker_code}  ${c.name}` }));
          }));
      }
    );

    this.selectSources[PO_FIELD.POINT_OF_CONTACT] = new LazyScrollableDataSource<SelectOption>(
      (page, _limit, search) => {
        if (page > 0) {
          return of([]);
        }
        return this._contactCompanyId$.pipe(
          take(1),
          switchMap(id => {
            if (!id) {
              return of([]);
            }
            return this.financesService.searchContacts(id, search || undefined)
              .pipe(map(res => res.data.map(c => ({ id: c.id, name: `${c.name}  ·  ${c.email}` }))));
          })
        );
      }
    );
  }

  // PR #38: companies with is_oc_required=true are not eligible for the Trato flow — render them
  // disabled with a tooltip. In other source types every company is selectable. The datasource is
  // rebuilt on sourceType changes (see ngOnChanges), so this re-evaluates live.
  private _toCompanyOption(company: Company): SelectOption {
    const ocRequired = this.sourceType === SOURCE_TYPE.TRATO && !!company.is_oc_required;
    return {
      id: company.id,
      name: `${company.maker_code}  ${company.name}`,
      ...(ocRequired ? { disabled: true, tooltip: { text: COMPANY_OC_REQUIRED_TOOLTIP, hover: true, position: 'right' as const } } : {}),
    };
  }
}
